import { useState } from "react";
import { PropertySearch } from "@/components/PropertySearch";
import { PropertyReport } from "@/components/PropertyReport";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LoadingProgress } from "@/components/LoadingProgress";
import { fetchQuickFacts, fetchPropertyReport, APIError } from "@/services/api";
import { toast } from "@/hooks/use-toast";
import { usePageMeta } from "@/hooks/usePageMeta";

const Index = () => {
  // Set canonical URL for homepage to prevent duplicate indexing
  usePageMeta({
    title: "Let's Be Real - Property Reports Made Simple",
    description: "Get honest, AI-powered property reports for Australian real estate. No sugar coating, just real data.",
    canonical: "https://letsbereal.com.au/",
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "website",
    twitterCard: "summary_large_image",
    keywords: "property reports, australian real estate, property analysis, census data, property insights, neighborhood analysis, home buyer research",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Let's Be Real",
      "url": "https://letsbereal.com.au",
      "description": "Honest, AI-powered property reports for Australian real estate",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://letsbereal.com.au/?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  });

  const [isLoadingQuickFacts, setIsLoadingQuickFacts] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [quickFacts, setQuickFacts] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [searchingAddress, setSearchingAddress] = useState<string>("");

  const handleSearch = async (address: string) => {
    // Progressive loading: Phase 1 - Quick Facts (5-8s)
    setIsLoadingQuickFacts(true);
    setQuickFacts(null);
    setReport(null);
    setSearchingAddress(address);
    setLoadingStep(0); // Finding address
    
    try {
      // Step 1: Start geocoding
      setLoadingStep(0);
      
      // Small delay to show the first step
      await new Promise(resolve => setTimeout(resolve, 500));
      setLoadingStep(1); // Gathering data
      
      // Fetch quick facts first (no AI, just data)
      const facts = await fetchQuickFacts(address);
      setQuickFacts(facts);
      setIsLoadingQuickFacts(false);
      setLoadingStep(2); // Analyzing with AI
      
      // Phase 2: Fetch full report with AI enhancement (in background)
      setIsLoadingAI(true);
      try {
        const propertyReport = await fetchPropertyReport(address);
        setLoadingStep(3); // Generating insights
        setReport(propertyReport);
      } catch (aiError) {
        // If AI fails, still show quick facts (graceful degradation)
        console.error('AI enhancement failed:', aiError);
        toast({
          title: "Partial results",
          description: "AI insights unavailable, but data loaded successfully.",
          variant: "default",
        });
      } finally {
        setIsLoadingAI(false);
        setSearchingAddress("");
      }
      
    } catch (error) {
      // Quick facts failed - show error
      setIsLoadingQuickFacts(false);
      setIsLoadingAI(false);
      setSearchingAddress("");
      setLoadingStep(0);
      
      if (error instanceof APIError) {
        toast({
          title: "Oops!",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Oops!",
          description: "Hmm… couldn't generate that report. Try another address!",
          variant: "destructive",
        });
      }
    }
  };

  const handleReset = () => {
    setQuickFacts(null);
    setReport(null);
    setIsLoadingQuickFacts(false);
    setIsLoadingAI(false);
    setSearchingAddress("");
    setLoadingStep(0);
  };

  // Show report if we have quick facts OR full report
  const hasData = quickFacts || report;
  const isLoading = isLoadingQuickFacts;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header onLogoClick={hasData || isLoading ? handleReset : undefined} />
      <div className="flex-1 flex items-center justify-center p-6">
        {isLoading ? (
          <LoadingProgress 
            currentStep={loadingStep} 
            address={searchingAddress}
            estimatedTime={20}
          />
        ) : !hasData ? (
          <PropertySearch onSearch={handleSearch} isLoading={isLoading} />
        ) : (
          <PropertyReport 
            report={report} 
            quickFacts={quickFacts}
            isLoadingAI={isLoadingAI}
            onReset={handleReset} 
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Index;
